import {
  buildMatchMapLoad,
  createTurretsFromPlan,
  createSpawnersFromPlan
} from '../../application/matchMapLoad.js';
import { createJungleCampState } from '../../domain/jungle.js';
import { MeshFactory } from '../factory/meshFactory.js';
import {
  BoxGeometry,
  CircleGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  RingGeometry,
  AdditiveBlending,
  DoubleSide
} from 'three';
import { buildAramSceneGroup } from '../scene/aramSceneBuilder.js';
import { syncBrushMeshes } from '../scene/brushSceneManager.js';
import {
  buildFowOverlayGroup
} from '../scene/fowOverlaySceneManager.js';
import {
  ARAM_BRIDGE_LENGTH,
  ARAM_BRIDGE_WIDTH
} from '../../domain/map/aramScale.js';
import { ARAM_BOUND_PAD } from '../../domain/map/aramBridgeCollision.js';
import { placeChampionsOnMapSpawn } from './placeChampionOnMapSpawn.js';
import { applyAramStartingLoadouts } from '../../domain/map/aramRules.js';
import {
  filterTrainingSecondaries,
  applyTrainingExtraVisibility,
  mapAllowsTrainingExtras
} from './mapSandboxExtras.js';

/**
 * Live map load (P3-02) — presentation applier + factory for app.js wiring.
 */
export function createLiveMapLoader(deps) {
  const sceneState = deps._mapSceneState || (deps._mapSceneState = {
    brushGroups: [],
    aramDispose: null,
    fowClearRoot: null
  });
  return function loadLiveMap(mapId) {
    const world = {
      mapBounds: deps.mapBounds,
      bushZones: deps.bushZones,
      wallBoxes: deps.wallBoxes,
      impassableZones: deps.impassableZones,
      turretRenderObjects: deps.getTurretRenderObjects()
    };
    return applyLiveMatchMapLoad({
      mapId,
      matchRuntime: deps.matchRuntime,
      world,
      sceneState,
      mapObjectives: deps.mapObjectives,
      scene: deps.scene,
      gridHelper: deps.gridHelper,
      hudUI: deps.hudUI,
      turretsUiContainer: deps.turretsUiContainer,
      championEntity: deps.championEntity,
      secondaryChampions: deps.secondaryChampions,
      attachDamageCallbacks: deps.attachDamageCallbacks,
      onTurretsRebuilt: deps.onTurretsRebuilt,
      onSpawnersRebuilt: deps.onSpawnersRebuilt,
      onPlayerPlaced: deps.onPlayerPlaced,
      onTrainingExtrasChanged: deps.onTrainingExtrasChanged,
      garenGroup: deps.garenGroup,
      garenPlate: deps.garenPlate,
      dummyGroup: deps.dummyGroup,
      dummyPlateDiv: deps.dummyPlateDiv,
      sandboxPropGroups: deps.sandboxPropGroups,
      onJungleRebuilt: (camps) => {
        spawnJungleCampsFromConfigs({
          camps,
          scene: deps.scene,
          hudUI: deps.hudUI,
          uiContainer: deps.jungleUiContainer,
          getActive: deps.getActiveJungleCamps,
          setActive: deps.setActiveJungleCamps
        });
      }
    });
  };
}

export function spawnJungleCampsFromConfigs({
  camps,
  scene,
  hudUI,
  uiContainer,
  getActive,
  setActive
}) {
  const previous = getActive();
  previous.forEach((c) => {
    scene.remove(c.meshGroup.group);
    if (c.meshGroup.plateDiv && c.meshGroup.plateDiv.parentNode) {
      c.meshGroup.plateDiv.parentNode.removeChild(c.meshGroup.plateDiv);
    }
  });
  const next = [];
  (camps || []).forEach((conf) => {
    const campState = createJungleCampState(conf.id, { x: conf.x, z: conf.z });
    const meshGroup = MeshFactory.createJungleCampMesh(campState, hudUI, uiContainer);
    scene.add(meshGroup.group);
    next.push({ state: campState, meshGroup });
  });
  setActive(next);
}

export function applyLiveMatchMapLoad(ctx) {
  const plan = buildMatchMapLoad(ctx.mapId);
  ctx.matchRuntime.applyMapLoad(plan);
  applyWorldGeometry(ctx.world, plan);
  applyMapSceneGeometry(ctx);
  const secondaries = filterTrainingSecondaries(plan.mapId, ctx.secondaryChampions);
  const champions = [ctx.championEntity, ...secondaries].filter(Boolean);
  placeChampionsOnMapSpawn(champions, plan.mapId);
  applyAramStartingLoadouts(champions, plan.mapId);
  if (typeof ctx.onPlayerPlaced === 'function' && ctx.championEntity) {
    ctx.onPlayerPlaced(ctx.championEntity);
  }
  if (typeof ctx.onTrainingExtrasChanged === 'function') {
    ctx.onTrainingExtrasChanged(plan.mapId);
  } else {
    applyTrainingExtraVisibility({
      mapId: plan.mapId,
      garenGroup: ctx.garenGroup,
      garenPlate: ctx.garenPlate,
      dummyGroup: ctx.dummyGroup,
      dummyPlateDiv: ctx.dummyPlateDiv,
      sandboxPropGroups: ctx.sandboxPropGroups
    });
  }
  const turrets = rebuildTurrets(ctx, plan);
  const spawners = createSpawnersFromPlan(plan, turrets);
  if (typeof ctx.onSpawnersRebuilt === 'function') {
    ctx.onSpawnersRebuilt(spawners);
  }
  ctx.matchRuntime.seedSandbox({
    player: ctx.championEntity,
    champions: secondaries,
    structures: turrets
  });
  if (ctx.mapObjectives) {
    ctx.mapObjectives.loadFromMap(plan.mapId);
  }
  const jungleCamps = mapAllowsTrainingExtras(plan.mapId) ? plan.jungleCamps : [];
  if (typeof ctx.onJungleRebuilt === 'function') {
    ctx.onJungleRebuilt(jungleCamps);
  }
  return { plan, turrets, spawners };
}

function applyWorldGeometry(world, plan) {
  Object.assign(world.mapBounds, plan.bounds);
  replaceArray(world.bushZones, plan.brushes);
  replaceArray(world.wallBoxes, plan.wallBoxes);
  replaceArray(world.impassableZones, plan.impassableZones);
}

/** Builds / replaces the 3D scene geometry for the selected map. */
function applyMapSceneGeometry(ctx) {
  if (!ctx.scene) return;
  const state = ctx.sceneState || (ctx.sceneState = {
    brushGroups: [],
    aramDispose: null,
    fowClearRoot: null
  });
  if (state.aramDispose) {
    state.aramDispose();
    state.aramDispose = null;
  }
  state.fowClearRoot = null;
  state.brushGroups = syncBrushMeshes(
    ctx.scene,
    ctx.mapId === 'test_map' ? [] : (ctx.world?.bushZones || []),
    state.brushGroups,
    MeshFactory
  );
  if (ctx.mapId === 'aram_proving_grounds') {
    if (ctx.gridHelper) ctx.gridHelper.visible = false;
    const threeKit = {
      BoxGeometry,
      CylinderGeometry,
      Group,
      Mesh,
      MeshStandardMaterial,
      PlaneGeometry
    };
    const { group, dispose } = buildAramSceneGroup(threeKit);
    ctx.scene.add(group);

    const fowThree = {
      Group,
      Mesh,
      PlaneGeometry,
      CircleGeometry,
      RingGeometry,
      MeshBasicMaterial,
      AdditiveBlending,
      DoubleSide
    };
    const fow = buildFowOverlayGroup(fowThree, {
      width: ARAM_BRIDGE_WIDTH + ARAM_BOUND_PAD * 2,
      length: ARAM_BRIDGE_LENGTH + ARAM_BOUND_PAD * 2
    });
    ctx.scene.add(fow.group);
    state.fowClearRoot = fow.clearRoot;

    state.aramDispose = () => {
      ctx.scene.remove(group);
      dispose();
      ctx.scene.remove(fow.group);
      state.fowClearRoot = null;
      if (ctx.gridHelper) ctx.gridHelper.visible = true;
    };
  }
}

function rebuildTurrets(ctx, plan) {
  const turrets = createTurretsFromPlan(plan);
  if (typeof ctx.attachDamageCallbacks === 'function') {
    ctx.attachDamageCallbacks(turrets);
  }
  clearTurretMeshes(ctx.world.turretRenderObjects, ctx.scene);
  const renders = turrets.map((t) => ({
    entity: t,
    ...MeshFactory.createTurretMesh(t, ctx.hudUI, ctx.turretsUiContainer)
  }));
  renders.forEach((tr) => {
    if (tr.group && ctx.scene) ctx.scene.add(tr.group);
  });
  ctx.world.turretRenderObjects = renders;
  if (typeof ctx.onTurretsRebuilt === 'function') {
    ctx.onTurretsRebuilt(turrets, renders);
  }
  return turrets;
}

function replaceArray(target, next) {
  target.length = 0;
  for (const item of next) {
    target.push(item);
  }
}

function clearTurretMeshes(turretRenderObjects, scene) {
  if (!Array.isArray(turretRenderObjects)) return;
  turretRenderObjects.forEach((entry) => {
    if (entry.group && scene) scene.remove(entry.group);
    if (entry.plateDiv && entry.plateDiv.parentNode) {
      entry.plateDiv.parentNode.removeChild(entry.plateDiv);
    }
  });
}
